import {FC, useEffect} from 'react';
import {useRecoilState, useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';
import {TableToolbar} from './TableToolbar';
import {userAccountsQuery} from '../../../store/users/selectors';
import {SelectChangeEvent} from '@mui/material';
import {GridRowParams, GridSortModel} from '@mui/x-data-grid';
import {useDebouncePageable} from '../../../hooks/useDebouncePageable';
import {useEventBus} from '../../../hooks/useEventBus';
import {LoadableHelpers} from '../../../shared/helpers/loadableHelpers';
import {IPageableTable} from '../../../shared/types/pageable.types';
import {
  currentUserAccountIdState,
  userAccountControlModeState,
  userAccountControlState,
  userAccountsQueryTrigger,
  userAccountsState,
  userPageable,
  userSortQueryTrigger,
} from '../../../store/users/atoms';
import {Table} from '../../Table';
import {columns} from './columns';

const UsersTable: FC = () => {
  const [loadable, setUsers] = useRecoilStateLoadable(userAccountsQuery);
  const triggerQuery = useSetRecoilState(userAccountsQueryTrigger);
  const {totalElements, totalPages, content} = useRecoilValue(userAccountsState);
  const setModeOfUserAccountControl = useSetRecoilState(userAccountControlModeState);
  const setOpenUserAccountControl = useSetRecoilState(userAccountControlState);
  const setCurrentUser = useSetRecoilState(currentUserAccountIdState);
  const [sort, setSort] = useRecoilState(userSortQueryTrigger);
  const [pageable, setPageable] = useRecoilState(userPageable);
  const {
    pageableToDebounce: {page, size},
    setPageableToDebounce,
    resetPageable,
  } = useDebouncePageable<IPageableTable>(pageable, setPageable);
  const bus = useEventBus();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageableToDebounce({page: newPage, size});
  };

  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    const value = e.target.value.toString();
    setPageableToDebounce({page: 0, size: parseInt(value, 10)});
    localStorage.setItem('usersPerPage', value);
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSort([...model]);
  };

  const handleRowClick = ({row}: GridRowParams) => {
    setCurrentUser(row.uid);
    setModeOfUserAccountControl('EDIT');
    setOpenUserAccountControl(true);
  };

  useEffect(() => {
    LoadableHelpers.handle(loadable, {
      success: setUsers,
      error: bus?.emit,
    });

    return () => {
      if (LoadableHelpers.isLoading(loadable)) return;
      triggerQuery((v) => v + 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadable.state]);

  return (
    <>
      <TableToolbar isLoading={LoadableHelpers.isLoading(loadable)} resetPageable={resetPageable} />
      <Table
        name="users"
        loading={LoadableHelpers.isLoading(loadable)}
        rows={content}
        columns={columns}
        pageSize={size}
        sortModel={sort}
        totalPages={totalPages}
        totalCount={totalElements}
        currentPage={page}
        startPageFromZero
        onRowClick={handleRowClick}
        onPageSizeChange={handlePageSizeChange}
        onPaginationChange={handleChangePage}
        onSortModelChange={handleSortModelChange}
        getRowId={(item) => item.uid}
      />
    </>
  );
};

export default UsersTable;
